import React from "react";
import {
  AbsoluteFill,
  Easing,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { Sfx, Ticks } from "../../b2b/ui/Sfx";
import { FLOAT_SHADOW, LOOK } from "../../b2c/look";
import { Words } from "../../b2c/ui/Words";
import { alpha, mix } from "../color";
import { euros, useMerchant } from "../copy";
import { BEAT, type SceneProps } from "../timing";
import { shot } from "../merchants";
import { useStage } from "../stage";
import { FOOT_ROOM, footnoteBox } from "../ui/Footnote";
import { PhotoCard } from "../ui/Photo";

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/**
 * Local frames (spot). The card lands just before beat 1, the amount
 * settles near beat 2, and the friend's side follows soon after so the
 * whole offer holds on screen before the cut. The bumper's offer is 5 beats
 * instead of 7: everything lands sooner there (`at`), like the end card.
 */
const CARD = 10;
const COUNT_FROM = 12;
const COUNT_TO = 24;
const FRIEND = 32;

/**
 * Offer — hard cut to the brand's own colour. The ask ("recommend {name}
 * to your friends"), then the reward card: "up to {reward}" with its
 * mechanic on the same card ("on every order placed through your link")
 * and the friend's side right under it, so the amount is never read
 * alone. The landing's disclaimer, shortened, sits at the bottom.
 */
export const Offer: React.FC<SceneProps> = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const { m, t, copy, lang } = useMerchant();
  const stage = useStage();
  const c = m.colors;
  const k = durationInFrames < 6 * BEAT ? 0.7 : 1;
  const at = (f: number) => Math.round(f * k);
  const [cardAt, countFrom, countTo, friendAt] = [
    CARD,
    COUNT_FROM,
    COUNT_TO,
    FRIEND,
  ].map(at);
  // The hold keeps moving: slow push-in on the offer, light drifting down.
  const drift = interpolate(frame, [0, durationInFrames], [0, 1], clamp);

  const wide = stage.shape === "wide";
  const G = {
    tall: { head: 88, cardW: 900, amount: 210, text: 40 },
    feed: { head: 76, cardW: 820, amount: 180, text: 36 },
    square: { head: 64, cardW: 740, amount: 150, text: 32 },
    wide: { head: 92, cardW: 800, amount: 200, text: 38 },
  }[stage.shape];

  const card = spring({
    frame: frame - cardAt,
    fps,
    config: { damping: 15, stiffness: 170, mass: 0.7 },
  });
  const friend = spring({
    frame: frame - friendAt,
    fps,
    config: { damping: 13, stiffness: 210, mass: 0.6 },
  });
  const value = Math.round(
    interpolate(frame, [countFrom, countTo], [0, m.reward], {
      ...clamp,
      easing: Easing.out(Easing.cubic),
    }),
  );
  const settle = interpolate(
    frame,
    [countTo - 2, countTo + 2, countTo + 8],
    [1, 1.08, 1],
    clamp,
  );

  const rewardCard = (
    <div style={{ position: "relative", width: G.cardW, flex: "none" }}>
      {/* A print of the product, tucked behind the card's corner. */}
      <PhotoCard
        img={shot(m, 1)}
        x={G.cardW - G.cardW * 0.14}
        y={-G.cardW * 0.03}
        width={G.cardW * 0.32}
        at={cardAt + 6}
        rotate={9}
        from={[0.3, 0.8]}
        radius={20}
        style={{ zIndex: 0 }}
      />
      <div
        style={{
          position: "relative",
          padding: `${G.text * 1.1}px ${G.text * 1.2}px ${G.text * 1.2}px`,
          borderRadius: 52,
          background: LOOK.white,
          boxShadow: FLOAT_SHADOW,
          fontFamily: LOOK.body,
          color: LOOK.ink,
          translate: `0 ${(1 - card) * 60}%`,
          opacity: Math.min(1, card * 1.5),
        }}
      >
        <div
          style={{
            fontSize: G.text,
            fontWeight: LOOK.semi,
            color: LOOK.inkSoft,
          }}
        >
          {copy.offer.label}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: G.text * 0.4,
            flexWrap: "wrap",
            color: c.onWhite,
            fontFamily: LOOK.display,
            fontWeight: LOOK.bold,
          }}
        >
          <span style={{ fontSize: G.amount * 0.3, letterSpacing: -1 }}>
            {copy.upTo}
          </span>
          <span
            style={{
              fontSize: G.amount,
              lineHeight: 1,
              letterSpacing: -G.amount * 0.045,
              fontVariantNumeric: "tabular-nums",
              scale: settle,
              transformOrigin: "0% 80%",
            }}
          >
            {euros(lang, value)}
          </span>
        </div>
        <div
          style={{
            fontSize: G.text,
            fontWeight: LOOK.medium,
            marginTop: G.text * 0.2,
            textWrap: "balance",
          }}
        >
          {copy.offer.per}
        </div>
      </div>
    </div>
  );

  const friendChip = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: G.text * 0.5,
        padding: `${G.text * 0.5}px ${G.text * 0.8}px ${G.text * 0.5}px ${G.text * 0.55}px`,
        borderRadius: 999,
        background: alpha(c.onBrand, 0.12),
        border: `2px solid ${alpha(c.onBrand, 0.25)}`,
        color: c.onBrand,
        fontFamily: LOOK.body,
        fontSize: G.text * 0.92,
        scale: friend,
        maxWidth: G.cardW,
      }}
    >
      <span style={{ fontSize: G.text * 1.3, lineHeight: 1 }}>🎁</span>
      <span>
        <span style={{ fontWeight: LOOK.medium, opacity: 0.8 }}>
          {copy.offer.friend}
          {lang === "fr" ? "\u00A0: " : ": "}
        </span>
        <span style={{ fontWeight: LOOK.bold }}>{copy.offer.friendValue}</span>
      </span>
    </div>
  );

  const headline = (
    <Words
      text={t(copy.offer.head)}
      start={2}
      stagger={2}
      fontSize={G.head}
      color={c.onBrand}
      align={wide ? "left" : "center"}
      style={{ maxWidth: wide ? 760 : 900 }}
    />
  );

  return (
    <AbsoluteFill
      style={{
        background: `radial-gradient(120% 90% at 50% ${20 + drift * 16}%, ${c.brand} 50%, ${mix(c.brand, "#000000", 0.2)} 100%)`,
      }}
    >
      <AbsoluteFill style={{ scale: 1 + drift * 0.035 }}>
        {wide ? (
          <AbsoluteFill
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              padding: `0 ${stage.safe.right}px 60px ${stage.safe.left}px`,
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 44,
                alignItems: "flex-start",
              }}
            >
              {headline}
              {friendChip}
            </div>
            {rewardCard}
          </AbsoluteFill>
        ) : (
          <AbsoluteFill
            style={{
              alignItems: "center",
              justifyContent: "center",
              gap: G.text * (stage.shape === "square" ? 0.9 : 1.3),
              padding: `${stage.safe.top}px ${stage.safe.right}px ${stage.safe.bottom + FOOT_ROOM[stage.shape]}px ${stage.safe.left}px`,
            }}
          >
            {headline}
            {/* Room for the print peeking over the card (~0.14 card widths). */}
            <div style={{ height: G.cardW * 0.14 - G.text * 1.2 }} />
            {rewardCard}
            {friendChip}
          </AbsoluteFill>
        )}
      </AbsoluteFill>

      <div
        style={{
          ...footnoteBox(stage),
          color: alpha(c.onBrand, 0.85),
          // On screen from the start: it is the offer's fine print.
          opacity: interpolate(frame, [2, 8], [0, 1], clamp),
        }}
      >
        {copy.footnote}
      </div>

      <Ticks from={countFrom} to={countTo} every={2} volume={0.7} />
      <Sfx sound="coins" at={countTo} />
      <Sfx sound="pop" at={friendAt} volume={0.7} />
    </AbsoluteFill>
  );
};
